"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowRight,
  ShieldCheck,
  Heart,
  Users,
  Zap,
  Cloud,
  Code,
  School,
  Video,
  TrendingUp,
  Rocket,
  BrainCircuit,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import React from "react";

/**
 * Represents a service in the ecosystem.
 */
interface Service {
  id: string;
  slug: string;
  title: string;
  name?: string;
  description: string;
  icon: string;
  features: string[];
  color: string;
  themeColor?: string;
}

/**
 * Props for the ServicesTabs component.
 */
interface ServicesTabsProps {
  services: Service[];
}

const iconMap: Record<string, any> = {
  favorite: Heart,
  Heart: Heart,
  verified_user: ShieldCheck,
  ShieldCheck: ShieldCheck,
  groups: Users,
  Users: Users,
  bolt: Zap,
  Zap: Zap,
  cloud_done: Cloud,
  Cloud: Cloud,
  developer_mode: Code,
  Code: Code,
  school: School,
  School: School,
  movie_edit: Video,
  Video: Video,
  trending_up: TrendingUp,
  TrendingUp: TrendingUp,
  rocket_launch: Rocket,
  Rocket: Rocket,
  psychology: Users,
  BrainCircuit: BrainCircuit,
};

/** Tech Color config map for service styling. */
const COLOR_CONFIG: Record<
  string,
  { bg: string; text: string; shadow: string; border: string; lightBg: string }
> = {
  "brand-primary": {
    bg: "bg-[#0066ff]",
    text: "text-[#00f3ff]",
    shadow: "shadow-[0_0_20px_rgba(0,102,255,0.4)]",
    border: "border-[#0066ff]",
    lightBg: "bg-[#0066ff]/10",
  },
  "brand-accent": {
    bg: "bg-[#ff7a00]",
    text: "text-[#ff9d00]",
    shadow: "shadow-[0_0_20px_rgba(255,122,0,0.4)]",
    border: "border-[#ff7a00]",
    lightBg: "bg-[#ff7a00]/10",
  },
  "emerald-500": {
    bg: "bg-emerald-500",
    text: "text-emerald-400",
    shadow: "shadow-[0_0_20px_rgba(16,185,129,0.4)]",
    border: "border-emerald-500",
    lightBg: "bg-emerald-500/10",
  },
  "indigo-600": {
    bg: "bg-indigo-500",
    text: "text-indigo-400",
    shadow: "shadow-[0_0_20px_rgba(99,102,241,0.4)]",
    border: "border-indigo-500",
    lightBg: "bg-indigo-500/10",
  },
};

/**
 * Interactive tabbed component for displaying service ecosystem.
 * Desktop: vertical tab list on the left, detail panel on the right.
 * Mobile: stacked accordion-style cards.
 *
 * @param services - Array of service data
 * @param services - Array of service data
 */
const ServicesTabs: React.FC<ServicesTabsProps> = ({ services }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useLanguage();

  /** Handles tab click to switch active service. */
  const handleTabClick = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const activeService = services[activeIndex];
  const ActiveIcon = iconMap[activeService?.icon] || Zap;
  const colors =
    COLOR_CONFIG[activeService?.themeColor || activeService?.color] ||
    COLOR_CONFIG["brand-primary"];
  const safeFeatures = activeService?.features || [];

  return (
    <>
      {/* Desktop Layout */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-8">
        {/* Tab List */}
        <div className="col-span-4 space-y-3">
          {services.map((service, index) => {
            const serviceColors =
              COLOR_CONFIG[service.themeColor || service.color] ||
              COLOR_CONFIG["brand-primary"];
            const Icon = iconMap[service.icon];
            const isActive = index === activeIndex;

            return (
              <button
                key={`${service.id}-${index}`}
                onClick={() => handleTabClick(index)}
                className={`w-full text-left p-4 rounded-2xl transition-all duration-300 flex items-center gap-5 group cursor-pointer border-l-4 ${
                  isActive
                    ? `bg-[#0a192f] shadow-[0_10px_30px_rgba(0,0,0,0.5)] border-[#00f3ff] scale-[1.02] active-tab-glow`
                    : "bg-transparent border-transparent hover:bg-white/[0.03]"
                }`}
                style={{
                  borderColor: isActive
                    ? service.themeColor || "#00f3ff"
                    : "transparent",
                  boxShadow: isActive
                    ? `0 10px 30px rgba(0,0,0,0.5), 0 0 20px ${service.themeColor || "#00f3ff"}22`
                    : "none",
                }}
              >
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-all duration-300 border border-white/5 ${
                    isActive ? "shadow-lg" : ""
                  }`}
                  style={{
                    backgroundColor: isActive
                      ? service.themeColor || "var(--brand-primary)"
                      : `rgba(255, 255, 255, 0.05)`, // Very dark/subtle inactive background
                    color: isActive
                      ? "white"
                      : service.themeColor || "var(--brand-primary)",
                    boxShadow: isActive
                      ? `0 0 20px ${service.themeColor || "#00f3ff"}88`
                      : "none",
                  }}
                >
                  {(() => {
                    const IconComponent = iconMap[service.icon] || Zap;
                    return (
                      <IconComponent className="w-7 h-7 transition-colors drop-shadow-[0_0_5px_currentColor]" />
                    );
                  })()}
                </div>
                <div>
                  <h4
                    className={`font-extrabold text-lg transition-colors ${
                      isActive
                        ? "text-white"
                        : "text-slate-400 group-hover:text-white"
                    }`}
                  >
                    {t(`home.services.items.${service.slug}`).title ||
                      service.title ||
                      service.name}
                  </h4>
                  <p className="text-slate-500 text-xs font-medium mt-1 tracking-tight">
                    {(service.features || []).slice(0, 2).join(" · ")}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail Panel */}
        <div className="col-span-8">
          <div
            key={activeService.id}
            className="bg-[#0a192f]/80 backdrop-blur-xl rounded-[2.5rem] p-12 shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-[#00f3ff]/10 h-full flex flex-col justify-center animate-fade-in relative overflow-hidden"
          >
            {/* Tech Decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none"></div>

            <div className="flex items-center gap-6 mb-8 relative z-10">
              <div
                className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 border-white/10`}
                style={{
                  backgroundColor: activeService.themeColor || "#0066ff",
                }}
              >
                {(() => {
                  const IconComponent = iconMap[activeService.icon] || Zap;
                  return (
                    <IconComponent className="text-white w-10 h-10 drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" />
                  );
                })()}
              </div>
              <div>
                <h3 className="text-3xl font-extrabold text-white">
                  {t(`home.services.items.${activeService.slug}`).title ||
                    activeService.title ||
                    activeService.name}
                </h3>
                <div
                  className={`h-1 w-16 rounded-full mt-3 ${colors.shadow}`}
                  style={{
                    backgroundColor: activeService.themeColor || "#0066ff",
                  }}
                />
              </div>
            </div>
            <p 
              className="text-slate-300 text-lg leading-relaxed font-medium mb-10 relative z-10"
              dangerouslySetInnerHTML={{
                __html: t(`home.services.items.${activeService.slug}`).description || activeService.description
              }}
            />
            <div className="grid grid-cols-2 gap-4 mb-10 relative z-10">
              {safeFeatures.map((feat, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 border border-white/5 px-5 py-3.5 rounded-xl font-bold text-sm text-slate-200`}
                  style={{
                    backgroundColor: `${activeService.themeColor || "#0066ff"}1A`,
                  }} // 1A is 10% opacity in hex
                >
                  <ShieldCheck
                    className="w-5 h-5 shrink-0 drop-shadow-[0_0_5px_currentColor]"
                    style={{ color: activeService.themeColor || "#00f3ff" }}
                  />
                  {feat}
                </div>
              ))}
            </div>
            <Link
              className={`inline-flex items-center gap-2 font-extrabold text-lg group/link cursor-pointer drop-shadow-[0_0_8px_currentColor] transition-all relative z-10 w-fit hover:scale-105`}
              style={{ color: activeService.themeColor || "#00f3ff" }}
              href={`/ecosystem/${activeService.slug}`}
            >
              {t("home.services.viewDetail") || "Xem chi tiết"}
              <ArrowRight className="w-5 h-5 group-hover/link:translate-x-2 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Layout - Accordion Cards */}
      <div className="lg:hidden space-y-4">
        {services.map((service, index) => {
          const serviceColors =
            COLOR_CONFIG[service.themeColor || service.color] ||
            COLOR_CONFIG["brand-primary"];
          const Icon = iconMap[service.icon];
          const isActive = index === activeIndex;

          return (
            <div
              key={`${service.id}-${index}`}
              className="rounded-[2rem] overflow-hidden border border-[#00f3ff]/10"
            >
              <button
                onClick={() => handleTabClick(index)}
                className={`w-full text-left p-6 flex items-center gap-4 transition-all duration-300 cursor-pointer ${
                  isActive
                    ? "bg-[#0a192f] shadow-[0_0_20px_rgba(0,0,0,0.5)] border-b border-[#00f3ff]/10"
                    : "bg-[#040d21] hover:bg-[#0a192f]/50"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border border-white/5 transition-all duration-300`}
                  style={{
                    backgroundColor: isActive
                      ? service.themeColor || "var(--brand-primary)"
                      : `rgba(255, 255, 255, 0.05)`,
                    color: isActive
                      ? "white"
                      : service.themeColor || "var(--brand-primary)",
                    boxShadow: isActive
                      ? `0 0 15px ${service.themeColor || "#00f3ff"}66`
                      : "none",
                  }}
                >
                  {(() => {
                    const IconComponent = iconMap[service.icon] || Zap;
                    return (
                      <IconComponent
                        className={`w-6 h-6 transition-colors ${isActive ? "drop-shadow-[0_0_5px_rgba(255,255,255,0.8)]" : ""}`}
                      />
                    );
                  })()}
                </div>
                <h4
                  className={`font-extrabold text-lg ${
                    isActive ? "text-white" : "text-slate-400"
                  }`}
                >
                  {t(`home.services.items.${service.slug}`).title ||
                    service.title ||
                    service.name}
                </h4>
              </button>
              {isActive && (
                <div className="bg-[#0a192f]/80 backdrop-blur-md px-6 pb-6 pt-4 animate-fade-in">
                  <p 
                    className="text-slate-300 leading-relaxed font-medium mb-6"
                    dangerouslySetInnerHTML={{
                      __html: t(`home.services.items.${service.slug}`).description || service.description
                    }}
                  />
                  <div className="flex flex-wrap gap-3 mb-6">
                    {(service.features || []).map((feat, i) => (
                      <span
                        key={i}
                        className={`flex items-center gap-2 border border-white/5 px-4 py-2 rounded-lg text-sm font-bold text-slate-200`}
                        style={{
                          backgroundColor: `${service.themeColor || "#0066ff"}1A`,
                        }} // 10% opacity
                      >
                        <ShieldCheck
                          className="w-4 h-4"
                          style={{ color: service.themeColor || "#00f3ff" }}
                        />
                        {feat}
                      </span>
                    ))}
                  </div>
                  <Link
                    className={`inline-flex items-center gap-2 ${serviceColors.text} font-extrabold text-sm cursor-pointer drop-shadow-[0_0_5px_currentColor]`}
                    href={`/ecosystem/${service.slug}`}
                  >
                    {t("home.services.viewDetail") || "Xem chi tiết"}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ServicesTabs;
